import React, { Component } from "react";
import { Table } from "react-bootstrap";
import _ from "lodash";
import { connect } from "react-redux";
import { typeActions } from "../../../webapi";
import {
	Button,
	CheckBox,
	RadioButton,
	GenericInput,
	SVGIcon,
	Popup,
} from "../../../components";
import FontAwesome from "react-fontawesome";
import { requiresUserTermsAndConditions } from "../../../config";
import {
	hexToRGBAstring,
	COLOUR_GRAPEFRUIT,
	COLOUR_PURPLE,
	COLOUR_BRANDING_MAIN,
	COLOUR_TANGERINE,
	COLOUR_TEAL,
} from "../../../js";
import { PageTitle } from "../../../components/text";
import {
	extensionPermissions,
	extensionHiddenSections,
} from "../../../config/features";
import {
	PERMISSIONS_CONFIG,
	USER_FEATURES_CONFIG,
	SETTINGS_CONFIG,
	getImportanceInfo,
	isCriticalPermission,
	getAllConfigurableItemsByCategory,
	PERMISSION_CATEGORIES,
	IMPORTANCE_LEVELS,
} from "./permissionConfig";
import styles from "./PermissionType.module.css";

class PermissionType extends Component {
	constructor(props) {
		super(props);
		this.state = {
			saving: false,
			deleting: false,
			newType: {},
			permissions: [
				...PERMISSIONS_CONFIG.filter(
					(p) => p.category !== PERMISSION_CATEGORIES.EXTRAS,
				),
				// NOTE: if the extensions do no include the metadata such as category, etc. we assign sane default for backwards compatibility
				...extensionPermissions.map((p) => ({
					...p,
					category: p.category || PERMISSION_CATEGORIES.EXTRAS,
					helperText: p.helperText || "",
					importance: p.importance || IMPORTANCE_LEVELS.IMPORTANT,
				})),
			],
			sectionsToHide: [...extensionHiddenSections, ...USER_FEATURES_CONFIG],
			settings: SETTINGS_CONFIG,
			hqTypes: [
				...extensionPermissions.filter((p) => p.hq).map((p) => p.key),
				"userManagement",
				"services",
			].filter((key, index, self) => self.indexOf(key) === index),
			permissionSearchQuery: "",
			showCriticalModal: false,
			pendingPermissionKey: null,
			pendingAction: null,
		};
		this.colors = [
			COLOUR_GRAPEFRUIT,
			COLOUR_PURPLE,
			COLOUR_BRANDING_MAIN,
			COLOUR_TANGERINE,
			COLOUR_TEAL,
		];
	}

	UNSAFE_componentWillMount() {
		const typeCopy = _.cloneDeep(this.props.userType);
		if (_.isUndefined(typeCopy.permissions)) {
			typeCopy.permissions = [];
		}
		if (_.isUndefined(typeCopy.hidden)) {
			typeCopy.hidden = [];
		}
		if (_.isUndefined(typeCopy.settings)) {
			typeCopy.settings = {};
		}

		// NOTE: Inverting the values for backwards compatibility (the database store as "isHidden" but we now display as "isAllowed" (see PC-1314))
		USER_FEATURES_CONFIG.forEach((feature) => {
			if (typeCopy.settings[feature.key] === undefined) {
				typeCopy.settings[feature.key] = !typeCopy.hidden.includes(feature.key);
			}
		});

		this.setState({
			oldType: _.cloneDeep(typeCopy),
			newType: _.cloneDeep(typeCopy),
		});
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		const typeCopy = _.cloneDeep(nextProps.userType);
		if (_.isUndefined(typeCopy.permissions)) {
			typeCopy.permissions = [];
		}
		if (_.isUndefined(typeCopy.hidden)) {
			typeCopy.hidden = [];
		}
		if (_.isUndefined(typeCopy.settings)) {
			typeCopy.settings = {};
		}
		this.setState({ oldType: _.cloneDeep(typeCopy) });
	}

	getUserTypeOptions() {
		return [
			{
				Label: "Primary user",
				Value: "resident",
				onChange: () => this.setCategory("resident"),
			},
			{
				Label: "Staff",
				Value: "staff",
				onChange: () => this.setCategory("staff"),
			},
			{
				Label: "Linked user",
				Value: "family",
				onChange: () => this.setCategory("family"),
			},
		];
	}

	handleNameChange(value) {
		this.setState({
			newType: {
				...this.state.newType,
				displayName: value,
			},
		});
	}

	handleSearchChange(event) {
		this.setState({
			permissionSearchQuery: event.target.value,
		});
	}

	isSubmitting = () => {
		const { saving, deleting } = this.state;
		return saving || deleting;
	};

	handleCheckChange(permissionValue) {
		const { newType } = this.state;
		if (this.isSubmitting()) return;

		const permission = this.state.permissions.find(
			(p) => p.key === permissionValue,
		);
		const isEnabling = !_.find(
			newType.permissions,
			(o) => o === permissionValue,
		);
		const isCritical = isCriticalPermission(permission);

		// non critical can be toggle immediately
		if (!isCritical) {
			this.togglePermission(permissionValue);
			return;
		}

		this.setState({
			showCriticalModal: true,
			pendingPermissionKey: permissionValue,
			pendingAction: isEnabling ? "enable" : "disable",
		});
		return;
	}

	openCriticalModal = () => {
		this.setState({
			showCriticalModal: true,
		});
	};

	closeCriticalModal = () => {
		this.setState({
			showCriticalModal: false,
			pendingPermissionKey: null,
			pendingAction: null,
		});
	};

	confirmCriticalChange = () => {
		const { pendingPermissionKey } = this.state;
		if (pendingPermissionKey) {
			this.togglePermission(pendingPermissionKey);
		}
		this.closeCriticalModal();
	};

	togglePermission = (permissionValue) => {
		const { newType } = this.state;

		if (_.findIndex(newType.permissions, (o) => o === permissionValue) > -1) {
			const newArray = newType.permissions;
			_.pull(newArray, permissionValue);
			this.setState({
				newType: {
					...newType,
					permissions: newArray,
				},
			});
		} else {
			let newArray = newType.permissions;
			if (!Array.isArray(newArray)) {
				newArray = [];
			}
			newArray.push(permissionValue);
			this.setState({
				newType: {
					...newType,
					permissions: newArray,
				},
			});
		}
	};

	handleSettingsChange(permissionValue) {
		const { newType } = this.state;
		if (this.isSubmitting()) return;

		const newSettings = _.cloneDeep(newType.settings);

		newSettings[permissionValue] = !newSettings[permissionValue];
		this.setState({
			newType: {
				...newType,
				settings: newSettings,
			},
		});
	}

	convertSettingsToHidden(settings) {
		const hidden = [];
		USER_FEATURES_CONFIG.forEach((feature) => {
			if (settings[feature.key] === false) {
				hidden.push(feature.key);
			}
		});
		return hidden;
	}

	handleSubmit() {
		const { newType } = this.state;
		if (this.isSubmitting()) return;
		if (!this.isChanged()) return;

		this.setState({ saving: true });

		const typeToSave = {
			...newType,
			hidden: this.convertSettingsToHidden(newType.settings),
		};

		typeActions
			.setTypePermissions(typeToSave, this.props.auth.user.uid)
			.then((res) => {
				this.props.updateUserType();
				setTimeout(() => {
					this.setState(
						{
							saving: false,
						},
						this.props.selectView(),
					);
				}, 1000);
			})
			.catch((error) => {});
	}

	confirmDeleteType() {
		if (this.isSubmitting()) return;

		if (!window.confirm("Are you sure you want to delete that user type?"))
			return;
		return this.handleDelete();
	}

	handleDelete() {
		if (this.isSubmitting()) return;

		this.setState({ deleting: true });
		typeActions
			.deleteUserType(this.props.auth.site, this.state.newType.typeName)
			.then((res) => {
				if (res.data.success) {
					this.props.updateUserType();
				} else if (res.data.existingUsers) {
					this.props.showExistingUsersOnDelete();
					this.setState({
						deleting: false,
					});
				}
			})
			.catch((error) => {});
	}

	isChanged() {
		const { oldType, newType } = this.state;
		if (this.isSubmitting()) return false;

		if (this.props.selectedView.isNew) return true;
		if (oldType.displayName !== newType.displayName) return true;
		if (oldType.category !== newType.category) return true;

		if (!_.isEqual(oldType.permissions.sort(), newType.permissions.sort()))
			return true;

		if (
			_.some(Object.keys(newType.settings), (key) => {
				return (
					!oldType.settings || oldType.settings[key] !== newType.settings[key]
				);
			})
		) {
			return true;
		}

		if (
			oldType.settings &&
			_.some(Object.keys(oldType.settings), (key) => {
				return !_.has(newType.settings, key);
			})
		) {
			return true;
		}

		return false;
	}

	onCloseEdit = () => {
		if (
			this.isChanged() &&
			!window.confirm(
				"You have unsaved changes. Are you sure you want to continue?",
			)
		)
			return;
		this.props.selectView();
	};

	setCategory(val) {
		this.setState({
			newType: {
				...this.state.newType,
				category: val,
			},
		});
	}

	getIconColor = () => {
		const colorIndex = (this.props.index + 1) % this.colors.length;
		return this.colors[
			colorIndex === 0 ? this.colors.length - 1 : colorIndex - 1
		];
	};

	isMyType = () => {
		if (!this.props.auth.user || !this.props.auth.user.Roles) {
			return false;
		}
		return _.some(this.props.auth.user.Roles, (r) => {
			return (
				r.site === this.state.newType.site &&
				r.type === this.state.newType.typeName
			);
		});
	};

	getPermissionText = (key, text) => {
		if (
			!this.props.strings ||
			!this.props.strings.permission ||
			!this.props.strings.permission[key]
		) {
			return text;
		}
		return this.props.strings.permission[key];
	};

	getItemValue = (item) => {
		if (item.type === "permission") {
			return (
				_.findIndex(this.state.newType.permissions, (o) => o === item.key) > -1
			);
		} else if (item.type === "userFeature" || item.type === "setting") {
			return !!this.state.newType.settings[item.key];
		}
		return false;
	};

	toggleItem = (item) => {
		if (item.type === "permission") {
			this.handleCheckChange(item.key);
		} else if (item.type === "userFeature" || item.type === "setting") {
			this.handleSettingsChange(item.key);
		}
	};

	renderAllConfigurableItems() {
		const isMyType = this.isMyType();
		const { permissionSearchQuery } = this.state;

		const filteredPermissions = this.state.permissions.filter((p) => {
			if (permissionSearchQuery) {
				const query = permissionSearchQuery.toLowerCase();
				const displayName = this.getPermissionText(
					p.key,
					p.displayName,
				).toLowerCase();
				const helperText = (p.helperText || "").toLowerCase();
				if (!displayName.includes(query) && !helperText.includes(query)) {
					return false;
				}
			}
			if (p.key === "terms" && !requiresUserTermsAndConditions) return false;
			if (
				this.props.auth.site === "hq" &&
				!_.includes(this.state.hqTypes, p.key)
			) {
				return false;
			}
			return true;
		});

		const filteredUserFeatures = this.state.sectionsToHide.filter((f) => {
			if (permissionSearchQuery) {
				const query = permissionSearchQuery.toLowerCase();
				const displayName = (f.displayName || f.text || "").toLowerCase();
				const helperText = (f.helperText || "").toLowerCase();
				if (!displayName.includes(query) && !helperText.includes(query)) {
					return false;
				}
			}
			return true;
		});

		const filteredSettings = this.state.settings.filter((s) => {
			if (permissionSearchQuery) {
				const query = permissionSearchQuery.toLowerCase();
				const displayName = (s.displayName || s.text || "").toLowerCase();
				const helperText = (s.helperText || "").toLowerCase();
				if (!displayName.includes(query) && !helperText.includes(query)) {
					return false;
				}
			}
			return true;
		});

		const groupedItems = getAllConfigurableItemsByCategory(
			filteredPermissions,
			filteredUserFeatures,
			filteredSettings,
		);

		return Object.entries(groupedItems)
			.filter(([_, items]) => {
				return items && items.length > 0;
			})
			.map(([category, items]) => {
				const renderList = () => {
					return items.map((ev) => {
						const isDisabled =
							ev.type === "permission" &&
							ev.key === "permissionManagement" &&
							isMyType;
						const importance = ev.importance || IMPORTANCE_LEVELS.IMPORTANT;
						const importanceInfo = getImportanceInfo(importance);
						const isActive = this.getItemValue(ev);

						return (
							<tr
								key={`${this.state.newType.Id}_${ev.type}_${ev.key}`}
								className={`permission-${importance} ${styles.permissionRow} ${
									isDisabled ? styles.disabled : ""
								}`}
								style={{
									"--border-color": importanceInfo.borderColor,
									"--bg-color": importanceInfo.backgroundColor,
									"--badge-bg": importanceInfo.backgroundColor,
									"--badge-color": importanceInfo.textColor,
									"--badge-border": importanceInfo.borderColor,
								}}
								onClick={() => {
									if (!isDisabled) {
										this.toggleItem(ev);
									}
								}}
							>
								<td className={styles.checkboxCell}>
									<CheckBox
										id={`${ev.type}_${ev.key}`}
										isActive={isActive}
										onChange={(e) => {
											e.stopPropagation();
											this.toggleItem(ev);
										}}
										className={styles.checkboxWrapper}
										disabled={isDisabled}
									/>
								</td>
								<td className="text">
									<div className="permissionRow">
										<div className="permissionTitle">
											<span className="permissionName">
												{ev.type === "permission"
													? this.getPermissionText(ev.key, ev.displayName)
													: ev.displayName}
												{isDisabled ? (
													<span className="text-italic">
														{" "}
														(unable to remove this permission from yourself)
													</span>
												) : (
													""
												)}
											</span>
										</div>
										{ev.helperText && (
											<span className="help-text" title={ev.helperText}>
												{ev.helperText}
											</span>
										)}
										<div className="permission-tags">
											{ev.importance === IMPORTANCE_LEVELS.CRITICAL && (
												<span
													className={`importance-badge ${styles.importanceBadge}`}
												>
													{importanceInfo.label}
												</span>
											)}
										</div>
									</div>
								</td>
							</tr>
						);
					});
				};

				return (
					<div key={category}>
						<p className="subHeadingText">{category}</p>
						<Table className="plussTable" striped bordered condensed hover>
							<tbody>{renderList()}</tbody>
						</Table>
					</div>
				);
			});
	}

	renderPermissionSearch() {
		return (
			<div className={styles.searchSection}>
				<p className="subHeadingText">PERMISSIONS</p>
				<div className="searchInputWrapper">
					<FontAwesome name="magnifying-glass" className="searchIcon" />
					<GenericInput
						type="text"
						placeholder="Search permissions..."
						value={this.state.permissionSearchQuery}
						onChange={(e) => this.handleSearchChange(e)}
						inputClass="permissionSearchInput"
					/>
				</div>
			</div>
		);
	}

	renderSelectType() {
		return (
			<div className="selectUserType">
				<p className="subHeadingText">SELECT TYPE</p>
				<RadioButton
					isActive={this.state.newType.category}
					className="userTypeOptions"
					rowStyle={{ flexDirection: "row" }}
					options={this.getUserTypeOptions()}
				/>
			</div>
		);
	}
	renderEdit() {
		const { selectedView, userType } = this.props;
		if (!selectedView || selectedView.Id !== userType.Id) return null;

		return (
			<div className="editContainer">
				<PageTitle style={{ marginBottom: 20 }}>User Type Details</PageTitle>
				{this.renderNameInput()}
				{this.renderPermissionsEdit()}
				{this.renderEditButtons()}
			</div>
		);
	}

	renderNameInput() {
		const iconColor = this.getIconColor();

		return (
			<div className="nameInputSection">
				<div
					className="iconContainer"
					style={{ backgroundColor: hexToRGBAstring(iconColor, 0.4) }}
				>
					<SVGIcon
						colour={iconColor}
						icon="userType"
						width="43px"
						height="34px"
					/>
				</div>
				<GenericInput
					type="text"
					placeholder={this.state.oldType.displayName}
					value={this.state.newType.displayName}
					onChange={(e) => this.handleNameChange(e.target.value)}
					inputClass="userTypeNameInput"
					maxLength={100}
					className={styles.inputNoMargin}
				/>
			</div>
		);
	}

	renderPermissionsEdit() {
		return (
			<div>
				{this.renderSelectType()}
				{this.renderPermissionSearch()}
				{this.renderAllConfigurableItems()}
			</div>
		);
	}

	renderEditButtons() {
		const { saving, deleting } = this.state;

		return (
			<div className="buttonsSection">
				<Button
					buttonType="primary"
					onClick={() => this.handleSubmit()}
					isActive={this.isChanged()}
				>
					{saving ? "Saving..." : "Save"}
				</Button>
				<Button
					buttonType="outlined"
					onClick={() => this.confirmDeleteType()}
					isActive={!this.isSubmitting()}
					buttonStyle={{ marginRight: 16 }}
				>
					{deleting ? "Deleting..." : "Delete User Type"}
				</Button>
				<Button
					buttonType="tertiary"
					onClick={this.onCloseEdit}
					isActive={!this.isSubmitting()}
				>
					Cancel
				</Button>
			</div>
		);
	}

	renderCriticalModal() {
		const { showCriticalModal, pendingPermissionKey, pendingAction } =
			this.state;

		if (!showCriticalModal) return null;

		const permission = this.state.permissions.find(
			(p) => p.key === pendingPermissionKey,
		);
		if (!permission) return null;

		const displayName = this.getPermissionText(
			permission.key,
			permission.displayName,
		);
		const importanceInfo = getImportanceInfo(permission.importance);
		const action =
			pendingAction === "enable"
				? "gives users significant access"
				: "will restrict user access";

		return (
			<Popup
				show={showCriticalModal}
				onClose={this.closeCriticalModal}
				hasPadding
				title={`${pendingAction === "enable" ? "Enable" : "Disable"} Critical Permission`}
				buttons={[
					{
						type: "primary",
						text: `${pendingAction === "enable" ? "Enable" : "Disable"} Permission`,
						onClick: this.confirmCriticalChange,
						isActive: true,
					},
					{
						type: "tertiary",
						text: "Cancel",
						onClick: this.closeCriticalModal,
						isActive: true,
					},
				]}
			>
				<div className={styles.modalContent}>
					<div className="modalTitleWrapper">
						<p className="modal-title">{displayName}</p>
						<span
							className={`importance-badge ${styles.importanceBadge}`}
							style={{
								"--badge-bg": importanceInfo.backgroundColor,
								"--badge-color": importanceInfo.textColor,
								"--badge-border": importanceInfo.borderColor,
							}}
						>
							{importanceInfo.label} Permission
						</span>
					</div>
					<p className={styles.modalHelperText}>{permission.helperText}</p>
					<p className="modalWarning">Warning: This {action}.</p>
				</div>
			</Popup>
		);
	}

	render() {
		const { newType } = this.state;
		const { selectedView, selectView } = this.props;
		const iconColor = this.getIconColor();

		return (
			<div
				className={`userType ${selectedView && selectedView.Id === newType.Id ? "selected" : ""}`}
			>
				<div
					className="iconContainer"
					style={{ backgroundColor: hexToRGBAstring(iconColor, 0.4) }}
				>
					<SVGIcon
						colour={iconColor}
						icon="userType"
						width="43px"
						height="34px"
					/>
				</div>
				<div className="userTypeName">{newType.displayName}</div>
				<Button
					inline
					buttonType="primaryAction"
					onClick={() => selectView(newType)}
					isActive
				>
					Settings
				</Button>
				{this.renderEdit()}
				{this.renderCriticalModal()}
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	const { auth } = state;
	return {
		auth,
		strings: (state.strings && state.strings.config) || {},
	};
};

export default connect(mapStateToProps, {})(PermissionType);
